import { Avatar } from '@components/avatar';
import { SchedulerSidebarEquipmentMemberProps } from './types';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { useTranslation } from 'react-i18next';
import { cn } from '@utils/cn';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useSchedulerContext } from '@contexts/scheduler';
import { useAccess } from '@hooks/access';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { useRef } from 'react';
import { SchedulerConflicts } from '../../conflicts';

export const SchedulerSidebarEquipmentMember = ({ data }: SchedulerSidebarEquipmentMemberProps) => {
  const { t } = useTranslation('app');
  const {
    active: { schedule: activeSchedule },
  } = useSchedulerContext();
  const { equipmentMembers, setState, conflictsByAssetId } = useSchedulerSidebar();
  const access = useAccess();
  const popoverRef = useRef<HTMLDivElement>(null);

  const {
    assetMembers: { options: assetMembersOptions, disabled: assetMembersDisabled },
  } = useDropdownsContext();

  const { assetId, name, conflicts, equipmentDeactivated, edit, manuallyAdded, internalId } = data;
  const liveConflicts = conflictsByAssetId[assetId] ?? conflicts;
  const alert =
    !edit && (equipmentDeactivated ? 'error' : liveConflicts.length ? 'warning' : undefined);

  const scheduleMembersOptions = assetMembersOptions.filter(
    (el) => el.id !== 'unassigned' && el.id !== activeSchedule.foreman?.id,
  );
  const scheduleMembersOptionsWithDeactivated = equipmentDeactivated
    ? [...scheduleMembersOptions, { id: assetId, name: name, internalId: internalId }]
    : scheduleMembersOptions;
  const filteredOptions = scheduleMembersOptionsWithDeactivated
    .filter(
      (option) => option.id === assetId || !equipmentMembers.some((m) => m.assetId === option.id),
    )
    .map((option) => ({
      value: option.id,
      label: `${option.name} ${option.internalId ? `(${option.internalId})` : ''}` || '',
    }));

  const handleEditMember = (selectedAssetId: string) => {
    if (!selectedAssetId) {
      setState((prev) => ({
        ...prev,
        equipmentMembers: prev.equipmentMembers.filter((m) => !!m.assetId),
      }));
      return;
    }
    const selected = scheduleMembersOptions.find((el) => el.id === selectedAssetId);
    const isInitialMemberSelected = selectedAssetId === assetId;
    if (isInitialMemberSelected) {
      setState((prev) => ({
        ...prev,
        equipmentMembers: prev.equipmentMembers.map((m) =>
          m.assetId === assetId ? { ...m, edit: false } : m,
        ),
      }));
    } else {
      setState((prev) => ({
        ...prev,
        defaultEquipment: false,
        equipmentMembers: prev.equipmentMembers.map((m) =>
          m.assetId === assetId
            ? {
                id: undefined,
                assetId: selectedAssetId,
                name: selected?.name || '',
                internalId: selected?.internalId || '',
                conflicts: [],
                edit: false,
              }
            : m,
        ),
      }));
    }
  };

  const handleSelectOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleEditMember(assetId);
    }
  };

  const handleRemoveMember = () => {
    setState((prev) => ({
      ...prev,
      defaultEquipment: assetId ? false : prev.defaultEquipment,
      equipmentMembers: prev.equipmentMembers.filter((m) => m.assetId !== assetId),
    }));
  };

  const removeButton = () => (
    <AppTooltip side="top" context={t('schedule.equipment.tooltip.remove')}>
      <Button
        disabled={access.readOnly}
        variant="ghost"
        className="h-fit p-1.5"
        onClick={handleRemoveMember}>
        <SvgConsumer id="trash" className="shrink-0 text-neutral-400" />
      </Button>
    </AppTooltip>
  );

  if (edit) {
    return (
      <>
        <div className="col-start-1 col-end-4 flex items-center">
          <div ref={popoverRef} className="overflow-visible" />
          <MultiSelect
            value={assetId ? [assetId] : []}
            onChange={(v) => {
              handleEditMember(v[0] || assetId);
            }}
            disabled={access.readOnly || assetMembersDisabled || filteredOptions.length === 0}
            onOpenChange={handleSelectOpenChange}
            className="h-7 px-2.5 text-xs font-medium"
            defaultOpen
            singleMode
            popoverRef={popoverRef}
            options={filteredOptions}
          />
        </div>
        <div className="col-start-4 flex items-center justify-end">{removeButton()}</div>
      </>
    );
  }

  return (
    <>
      <div className="col-start-1 w-fit">
        {alert === 'warning' && (
          <SchedulerConflicts
            name={name}
            title={t('schedule.equipment.tooltip.conflict.title')}
            conflicts={liveConflicts}
            iconClassName="text-lg"
          />
        )}
        {alert === 'error' && (
          <AppTooltip context={t('schedule.equipment.tooltip.error')}>
            <SvgConsumer id="circleError" className="shrink-0 text-lg text-danger-400" />
          </AppTooltip>
        )}
      </div>
      <div className="col-start-2 flex items-center gap-2.5">
        <div className="relative shrink-0">
          <Avatar
            name={alert === 'error' ? '' : name}
            hideTooltip
            noOfInitials={1}
            className={cn('h-6 w-6 text-xs', alert === 'error' && 'bg-neutral-200')}>
            {alert === 'error' && (
              <SvgConsumer className="shrink-0 text-sm text-white" id="inactiveEquipment" />
            )}
          </Avatar>
          {manuallyAdded && (
            <AppTooltip side="bottom" context={t('schedule.equipment.tooltip.manuallyAdded')}>
              <span className="absolute -bottom-1.5 -right-1.5 flex h-1.5 w-1.5 p-1.5 bg-white items-center justify-center rounded-full p-2">
                <SvgConsumer id="manuallyAdded" className="shrink-0 text-blue-400 text-xs" />
              </span>
            </AppTooltip>
          )}
        </div>
        <div className="flex flex-col">
          <strong
            className={cn(
              'text-xs font-semibold',
              alert === 'error' ? 'text-neutral-300 line-through' : 'text-neutral-600',
            )}>
            {name}
          </strong>
          {internalId && (
            <span className="text-[10px] leading-3 text-neutral-400">{`#${internalId}`}</span>
          )}
        </div>
      </div>
      <div className="col-start-3 flex items-center justify-end">
        <AppTooltip side="top" context={t('schedule.equipment.tooltip.replace')}>
          <Button
            variant="ghost"
            disabled={access.readOnly}
            className="h-fit p-1.5"
            onClick={() => {
              if (edit) return;
              setState((prev) => ({
                ...prev,
                equipmentMembers: prev.equipmentMembers.map((m) =>
                  m.assetId === assetId ? { ...m, edit: true } : m,
                ),
              }));
            }}>
            <SvgConsumer id="replacePerson" className="shrink-0 text-neutral-400" />
          </Button>
        </AppTooltip>
      </div>
      <div className="col-start-4 flex items-center justify-end">{removeButton()}</div>
    </>
  );
};
